// ════════════════════════════════════════════════════════════════════════════
// 🕐 ShiftManager — Phase 7 Workforce & Shift Management
// ════════════════════════════════════════════════════════════════════════════
// Manager-only full-screen view to:
//   - Browse all shifts (clock-in / clock-out sessions) per store
//   - Filter by date range, cashier, status
//   - Edit shift details (manager override for times, notes, status)
//   - View per-shift sales aggregates (total revenue, transaction count)
//   - Export / copy timesheet data
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Clock, Calendar, Filter, Download, Edit3, Save, X, Loader2,
  CheckCircle2, AlertCircle, Timer, DollarSign, Receipt, User,
  Play, Square, ClockAlert, Wrench,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import type { CashierSession } from './CashierAuth';

interface ShiftRow {
  id: number;
  storeId: number;
  cashierId: number;
  cashier?: {
    id: number;
    name: string;
    username: string;
    role: string;
  };
  startedAt: string;
  endedAt: string | null;
  status: 'active' | 'completed' | 'auto_closed' | 'manual_adjusted';
  totalHours: number | null;
  notes: string | null;
  isAdjusted: boolean;
  totalSales: number;
  txCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ShiftManagerProps {
  storeApiKey: string;
  currentCashier: CashierSession | null;
  onBack: () => void;
}

const STATUS_META: Record<ShiftRow['status'], { label: string; badge: string; icon: any }> = {
  active: { label: 'Active', badge: 'bg-amber-500/10 border-amber-500/20 text-amber-400', icon: Play },
  completed: { label: 'Completed', badge: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
  auto_closed: { label: 'Auto-Closed', badge: 'bg-rose-500/10 border-rose-500/20 text-rose-400', icon: ClockAlert },
  manual_adjusted: { label: 'Adjusted', badge: 'bg-violet-500/10 border-violet-500/20 text-violet-400', icon: Wrench },
};

function formatDuration(hours: number | null): string {
  if (hours == null) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

function formatLKR(n: number): string {
  return `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toInputDate(d: string | Date | null): string {
  if (!d) return '';
  const date = new Date(d);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

const ShiftManager: React.FC<ShiftManagerProps> = ({ storeApiKey, currentCashier, onBack }) => {
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const [from, setFrom] = useState<string>(toInputDate(todayStart));
  const [toFilter, setToFilter] = useState<string>(toInputDate(todayEnd));
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cashierFilter, setCashierFilter] = useState<string>('');

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ShiftRow | null>(null);
  const [editForm, setEditForm] = useState({ startedAt: '', endedAt: '', status: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('kangavena:cashierToken') : '';

  const fetchShifts = async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      qs.set('storeApiKey', storeApiKey);
      if (from) qs.set('from', new Date(from).toISOString());
      if (toFilter) qs.set('to', new Date(toFilter).toISOString());
      if (statusFilter) qs.set('status', statusFilter);
      if (cashierFilter) qs.set('cashierId', cashierFilter);

      const res = await fetch(`/api/shifts?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load shifts');
      setShifts(data.shifts || []);
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [storeApiKey, setShifts, setLoading, setError]); // fetchShifts captures filter state — mount-only load

  // Apply filters on button click
  const applyFilters = () => {
    Haptics.fire('tap');
    fetchShifts();
  };

  const openEdit = (shift: ShiftRow) => {
    Haptics.fire('tap');
    setEditing(shift);
    setEditForm({
      startedAt: toInputDate(shift.startedAt),
      endedAt: toInputDate(shift.endedAt),
      status: shift.status,
      notes: shift.notes || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload: any = { notes: editForm.notes };
      if (editForm.startedAt) payload.startedAt = new Date(editForm.startedAt).toISOString();
      if (editForm.endedAt) payload.endedAt = new Date(editForm.endedAt).toISOString();
      else payload.endedAt = null;
      if (editForm.status) payload.status = editForm.status;

      const res = await fetch(`/api/shifts/${editing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setEditOpen(false);
      setEditing(null);
      fetchShifts();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Build unique cashier options from loaded shifts
  const cashierOptions = useMemo(() => {
    const map = new Map<number, string>();
    shifts.forEach((s) => {
      if (s.cashier) map.set(s.cashier.id, s.cashier.name);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [shifts]);

  // Top-line aggregates
  const stats = useMemo(() => {
    const totalHours = shifts.reduce((sum, s) => sum + (s.totalHours || 0), 0);
    const totalSales = shifts.reduce((sum, s) => sum + (s.totalSales || 0), 0);
    const txCount = shifts.reduce((sum, s) => sum + (s.txCount || 0), 0);
    const activeCount = shifts.filter((s) => s.status === 'active').length;
    return { totalHours, totalSales, txCount, activeCount };
  }, [shifts]);

  // Copy to clipboard as CSV
  const copyCSV = () => {
    Haptics.fire('tap');
    const headers = ['Cashier', 'Started', 'Ended', 'Duration', 'Status', 'Sales', 'Tx Count', 'Notes'];
    const rows = shifts.map((s) => [
      s.cashier?.name || '—',
      new Date(s.startedAt).toLocaleString('en-LK'),
      s.endedAt ? new Date(s.endedAt).toLocaleString('en-LK') : '—',
      formatDuration(s.totalHours),
      STATUS_META[s.status].label,
      s.totalSales.toFixed(2),
      s.txCount,
      s.notes || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    navigator.clipboard.writeText(csv).then(() => {
      // subtle feedback — could wire a toast here
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#04060f] text-white overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#04060f]/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => { Haptics.fire('tap'); onBack(); }}
            className="w-9 h-9 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] flex items-center justify-center text-slate-300 transition-all shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={15} />
          </button>
          <div className="flex-1" />
          <h1 className="text-[13px] font-black uppercase tracking-widest text-white flex items-center gap-2">
            <Clock size={16} className="text-amber-400" />
            Shift Manager
          </h1>
          <div className="flex-1" />
          <button
            onClick={copyCSV}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-all text-[11px] font-bold uppercase tracking-widest text-slate-300"
          >
            <Download size={13} /> Copy CSV
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Hours', value: formatDuration(stats.totalHours), icon: Timer, color: 'text-amber-400' },
            { label: 'Revenue', value: formatLKR(stats.totalSales), icon: DollarSign, color: 'text-emerald-400' },
            { label: 'Transactions', value: String(stats.txCount), icon: Receipt, color: 'text-cyan-400' },
            { label: 'Active Shifts', value: String(stats.activeCount), icon: Play, color: 'text-violet-400' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-3.5">
              <div className="flex items-center gap-2 mb-1.5">
                <s.icon size={12} className={s.color} />
                <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{s.label}</span>
              </div>
              <div className="text-[15px] font-black text-white">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 space-y-3">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400">
            <Filter size={11} /> Filters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="space-y-1">
              <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">From</label>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">To</label>
              <input
                type="datetime-local"
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40 appearance-none"
              >
                <option value="" className="bg-[#0b0f1e]">All</option>
                <option value="active" className="bg-[#0b0f1e]">Active</option>
                <option value="completed" className="bg-[#0b0f1e]">Completed</option>
                <option value="auto_closed" className="bg-[#0b0f1e]">Auto-Closed</option>
                <option value="manual_adjusted" className="bg-[#0b0f1e]">Adjusted</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Cashier</label>
              <select
                value={cashierFilter}
                onChange={(e) => setCashierFilter(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40 appearance-none"
              >
                <option value="" className="bg-[#0b0f1e]">All Cashiers</option>
                {cashierOptions.map(([id, name]) => (
                  <option key={id} value={String(id)} className="bg-[#0b0f1e]">{name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={applyFilters}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-500/20 transition-all"
              >
                <Calendar size={12} /> Apply
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-red-500/8 border border-red-500/20 text-red-400 text-[11px] font-mono">
            <AlertCircle size={12} /> {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
          <div className="grid grid-cols-[1.2fr_1fr_1fr_0.7fr_0.7fr_0.8fr_0.6fr_44px] gap-2 px-4 py-2.5 bg-white/[0.02] border-b border-white/[0.06] text-[9px] uppercase tracking-widest font-bold text-slate-500">
            <div>Cashier</div>
            <div>Started</div>
            <div>Ended</div>
            <div className="text-right">Duration</div>
            <div className="text-center">Status</div>
            <div className="text-right">Sales</div>
            <div className="text-right">Tx</div>
            <div />
          </div>

          {loading && shifts.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-[11px]">
              <Loader2 size={16} className="animate-spin mx-auto mb-2" /> Loading shifts…
            </div>
          )}

          {!loading && shifts.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-[11px]">
              No shifts match the current filters.
            </div>
          )}

          <div className="divide-y divide-white/[0.04]">
            {shifts.map((s) => {
              const meta = STATUS_META[s.status];
              const Icon = meta.icon;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-[1.2fr_1fr_1fr_0.7fr_0.7fr_0.8fr_0.6fr_44px] gap-2 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
                      <User size={11} className="text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-bold text-white truncate">{s.cashier?.name || '—'}</div>
                      <div className="text-[9px] text-slate-500 truncate">{s.cashier?.username || '—'}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-300 font-mono">{new Date(s.startedAt).toLocaleString('en-LK')}</div>
                  <div className="text-[11px] text-slate-300 font-mono">
                    {s.endedAt ? new Date(s.endedAt).toLocaleString('en-LK') : <span className="text-slate-600 italic">Running…</span>}
                  </div>
                  <div className="text-right text-[11px] font-bold text-white">{formatDuration(s.totalHours)}</div>
                  <div className="flex justify-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[9px] font-bold uppercase tracking-wider ${meta.badge}`}>
                      <Icon size={9} /> {meta.label}
                    </span>
                  </div>
                  <div className="text-right text-[11px] font-mono text-emerald-400">{formatLKR(s.totalSales)}</div>
                  <div className="text-right text-[11px] font-mono text-slate-400">{s.txCount}</div>
                  <button
                    onClick={() => openEdit(s)}
                    className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.08] transition-all text-slate-400"
                  >
                    <Edit3 size={12} />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editOpen && editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setEditOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg bg-[#0b0f1e] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between">
                <h2 className="text-[12px] font-black uppercase tracking-widest text-white flex items-center gap-2">
                  <Edit3 size={14} className="text-amber-400" /> Edit Shift
                </h2>
                <button onClick={() => setEditOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                  <X size={14} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/8 border border-red-500/20 text-red-400 text-[11px]">
                    <AlertCircle size={11} /> {error}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Started</label>
                    <input
                      type="datetime-local"
                      value={editForm.startedAt}
                      onChange={(e) => setEditForm((p) => ({ ...p, startedAt: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Ended</label>
                    <input
                      type="datetime-local"
                      value={editForm.endedAt}
                      onChange={(e) => setEditForm((p) => ({ ...p, endedAt: e.target.value }))}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40 appearance-none"
                  >
                    <option value="active" className="bg-[#0b0f1e]">Active</option>
                    <option value="completed" className="bg-[#0b0f1e]">Completed</option>
                    <option value="auto_closed" className="bg-[#0b0f1e]">Auto-Closed</option>
                    <option value="manual_adjusted" className="bg-[#0b0f1e]">Manual Adjusted</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                    rows={3}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-[12px] text-white outline-none focus:border-amber-500/40 resize-none"
                    placeholder="Manager notes about this shift…"
                  />
                </div>
              </div>
              <div className="px-5 pb-5">
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShiftManager;
